import { filter, uniq } from 'lodash';

const CATEGORY = {
    BOOKS: 'books',
    TEXTBOOK: 'Textbook',
    CASE: 'Case',
    READING: 'Reading',
    ALL: 'all'
};
const SEARCH_FIELDS = {
    SEARCH_FIELDS_ISBN: [
        'isbn10',
        'isbn13'
    ]
};
const CSG_CONSTANTS = {
    GEO_ORIGIN_US: ['US'],
    GEO_ORIGIN_CA: ['US', 'CA'],
    SHARING_PARAM: "Public|public",
    UNSCRUBBED_USER: false,
    FIELDS_RETURN: [
        'name',
        'author',
        'title',
        'cfAuthor',
        'cfPublisher',
        'cfSeriesTitle',
        'category',
        'authors',
        'isbn10',
        'isbn13',
        'thumbnailUrl',
        'imageURL',
        'images',
        'descriptionTxt',
        'resourceId',
        'printTrimSize'
    ],
};
const specialCharsCannotEscape = new RegExp(/[`~!@#$%^&*—()_|+=?;",.<>\{\}\[\]]/g);
const specialCharToEscape = new RegExp(/(\\|\/|\:|\-)/g);
const specialCharAllRegex = new RegExp(/[`~!@#$%^&*—()_|+\-=?;:'",.<>\{\}\[\]\\\/]/g); // special chars to replace
const SPACE = ' ';
const OR_OPERATOR = '|';
const AND_OPERATOR = '+';
const WILDCARD_OPERATOR = '*';
const spaceRegEx = new RegExp(/\s+/g);
const EMPTY = '';
const stopWords = ['a', 'an', 'and', 'are', 'as', 'at', 'be', 'but', 'by', 'for', 'if', 'in', 'into', 'is', 'it', 'no', 'not', 'of', 'on', 'or', 'such', 'that', 'the', 'their', 'then', 'there', 'these', 'they', 'this', 'to', 'was', 'will', 'with'];

const isStopWord = (text) => {
    return stopWords.indexOf(text.trim().toLowerCase()) !== -1;
};


export function getCSGQueryString(searchType, searchFields = [], searchTerm, category, activeCollection) {
    const fields = getFieldsFilter(searchFields, searchTerm);
  
    if (searchType === CATEGORY.TEXTBOOK) {
        // for case, reading and chapters apply boosting based on trim size
        const categories = getCategoryFilter(category, OR_OPERATOR, false);
      
        return buildSearchQuery({
            fieldsFilter: fields,
            categoryFilter: categories,
            searchKeyword: searchTerm,
            activeCollection
        });
    } else {
        // for books no boosting normal query
        const categories = getCategoryFilter(categories, OR_OPERATOR, false);
        
        return buildSearchQuery({
            fieldsFilter: fields,
            categoryFilter: categories,
            searchKeyword: searchTerm,
            activeCollection
        });
    }
}


function getCategoryFilter(categories, operator, includeBrackets = true) {
    const categoryFilter = formKeyValueFilter(['category'], categories, operator, includeBrackets);
    return categoryFilter;
}

function getFieldsFilter(searchFields, searchKeyword) {
    const minimumChars = 3;
    const queryTerm = formQueryString(searchKeyword, minimumChars, true, false);
    const fieldsFilter = getFieldFilter(searchFields, getSearchQuery(searchKeyword, queryTerm, minimumChars), OR_OPERATOR);
    return fieldsFilter;
}

/*
 * Show All query formation
 */
const getSearchQuery = (keyword = EMPTY, queryTerm = EMPTY, minimumChars) => {
    let queryArray = [];
    let keywordEscape = keyword;
    
    if (keywordEscape.startsWith('"') && keywordEscape.endsWith('"')) {
        queryArray.push(escapeSpecialChars(keyword));
    } else {
        let splCharCombination = [
            { ignoreSplChar: false, ignoreAndQuery: false }, // Include Spl Char with +
            { ignoreSplChar: true, ignoreAndQuery: false } // Ignore Spl Char with +
        ];
      
        // if has more than one words in the search term
        if (queryTerm.split(spaceRegEx).length > 1) {
            splCharCombination.push({ ignoreSplChar: true, ignoreAndQuery: true }); // Ignore Spl Char without +
            splCharCombination.push({ ignoreSplChar: false, ignoreAndQuery: true }); // Include Spl Char without +
        }
        
        // Escape Keyword if Special Character Found
        if (specialCharAllRegex.test(keyword)) {
            keywordEscape = escapeSpecialChars(keyword);
        } else {
            splCharCombination = filter(splCharCombination, { ignoreSplChar: true });
        }
        
        // Query formation for spl character combination
        const keywordArr = uniq(queryArrayFormation({ keyword: keywordEscape, minimumChars, splCharCombination }));
        if (keywordArr && keywordArr.length > 0) {
            queryArray = queryArray.concat(keywordArr);
        }
    }
    
    return uniq(queryArray).join(` ${'OR'} `);
};

/*
 * Keyword formation
 */
const queryArrayFormation = ({ keyword, minimumChars, splCharCombination }) => {
    const queryArray = [];
    splCharCombination.forEach((splChar) => {
        const { ignoreSplChar, ignoreAndQuery } = splChar;
        const oSplChar = keywordBuilder({ keyword, minimumChars, ignoreSplChar, ignoreAndQuery });
        if (oSplChar) {
            queryArray.push(oSplChar);
        }
    });
    return queryArray;
};

/*
 * Keyword builder
 */
const keywordBuilder = ({ keyword, minimumChars, ignoreSplChar, ignoreAndQuery }) => {
    const optional_queryTerm = formQueryString(keyword, minimumChars, ignoreSplChar, ignoreAndQuery);
    if (optional_queryTerm) {
        return `(${optional_queryTerm})`;
    }
};

const formQueryString = (keyword = EMPTY, minimumChars = default_minchars, ignoreSplChar = true, ignoreAndQuery = false) => {
    keyword = keyword.trim();
    // sanity, ignore special chars and replace empty and if the length is less than min chars, return
    if (removeSpecialChars(keyword).length < minimumChars) {
        return;
    }
    
    const newQueryString = [];
    
    // if ISBN, then return only this query
    if (isNaN(keyword) === false) {
        return `${keyword}${WILDCARD_OPERATOR}`;
    }
    
    const words = keyword.split(spaceRegEx);
    words.forEach((word) => {
        let splitWords = [word.trim()]; // default use the actual word, else if ignore special chars, then split it further
        if (ignoreSplChar) {
            splitWords = word.trim().split(specialCharAllRegex);
        } else {
            splitWords = word.trim().split(specialCharsCannotEscape);
        }
        
        if (ignoreAndQuery === true) {
            splitWords = splitWords.filter(v => {
                return v.length >= minimumChars;
            });
        }
      
        splitWords.forEach((splitWord) => {
            const text = splitWord.trim();
            if (text.length === 0) {
                return;
            }
            
            let _POST = WILDCARD_OPERATOR;
            if (text.length === 1) {
                _POST = EMPTY;
            }
            let _PRE = AND_OPERATOR;
            if (isStopWord(text) === true || ignoreAndQuery === true) {
                _PRE = EMPTY;
            }
            newQueryString.push(`${_PRE}${text}${_POST}`);
        });
    });
    
    let queryString = newQueryString.join(SPACE);
    if (words[words.length - 1].length === 1 && !queryString.endsWith(WILDCARD_OPERATOR)) {
        queryString += WILDCARD_OPERATOR;
    }
    return queryString;
};

const removeSpecialChars = (term) => {
    return term.replace(specialCharAllRegex, EMPTY);
};

const escapeSpecialChars = (text) => {
    if (text) {
        // escape spl chars listed in the specialCharToEcape list
        return `${text.replace(specialCharToEscape, '\\$1')}`;
    }
};

function getFieldFilter(fields, query, operator) {
    if (fields.length === 0) {
        return `(${query})`;
    }
    
    const fieldsFilter = formKeyValueFilter(fields, query, operator, false  );
    return fieldsFilter;
}

const formKeyValueFilter = (keys, query, operator, includeBrackets = true) => {
    if (Array.isArray(query)) {
        query = query.join(` ${operator} `);
    }
    
    const fieldsArray = [];
    keys.forEach((key) => {
        if (includeBrackets) {
            fieldsArray.push(`${key}:(${query})`);
        } else {
            fieldsArray.push(`${key}:${query}`);
        }
    });
    
    let queryWithFilter = fieldsArray.join(` ${operator} `);
    
    if (queryWithFilter.length > 0) {
        if (includeBrackets) {
            queryWithFilter = `(${queryWithFilter})`;
        } else {
            queryWithFilter = `${queryWithFilter}`;
        }
    }
    return queryWithFilter;
};


// scrubbed/unscrubbed flag filter
const getUnscrubbedFilter = () => {
    const unscrubbed = CSG_CONSTANTS.UNSCRUBBED_USER;
    return `unscrubbed:${unscrubbed}`;
};

// geoOrigin filter
const getGeoOriginFilter = (activeCollectionGeo) => {
    let geoOrigin;
    if (activeCollectionGeo === "CA") {
        geoOrigin = CSG_CONSTANTS.GEO_ORIGIN_CA;
    } else {
        geoOrigin = CSG_CONSTANTS.GEO_ORIGIN_US;
    }

    // geoOrigin:US|CA if we have [US, CA] else geoOrigin:US will be sent
    if (geoOrigin.length > 1){
        return `geoOrigin:${geoOrigin.join('\|')}`;
    }
    return `geoOrigin:${geoOrigin[0]}`;
};


// build query, with fileds filter, category filter and other filter like sharing, geoorigin, unscrubbed..
// search query is for the /search API in CSG
function buildSearchQuery({ fieldsFilter, categoryFilter, searchKeyword, notMissingParams, activeCollection }) {
    const queryParts = { queryString: '', must: [], should:[], should_not: [] };
    queryParts.queryString = fieldsFilter;
    if (categoryFilter) {
        queryParts.must.push(categoryFilter);
    }
    if (notMissingParams) {
        queryParts.should_not = notMissingParams.replace(/NOT/g, '').split("AND").map(missingElment => missingElment.trim());
    }
    if (getSharingFilter()) {
        queryParts.must.push(getSharingFilter());
    }
    queryParts.must.push(getUnscrubbedFilter());
    const activeCollectionGeo = (activeCollection.geo) ? activeCollection.geo.code : null;
    queryParts.must.push(getGeoOriginFilter(activeCollectionGeo));
    if (isISBNFilter(searchKeyword)) {
        queryParts.should.push(...getISBNFilter(searchKeyword).split('|')); 
    }
    return queryParts;
}

// sharing property filter
const getSharingFilter = () => {
    const sharing = CSG_CONSTANTS.SHARING_PARAM;
    if (sharing) {
        return `sharing:${sharing}`;
    }
};

function getISBNFilter(searchKeyword){
    if (isISBNFilter(searchKeyword)) {
        return getFieldFilter(SEARCH_FIELDS.SEARCH_FIELDS_ISBN, searchKeyword, OR_OPERATOR);
    }
}

const isISBNFilter = (keyword) => {
    const isbn_regex = new RegExp(/^((\d{13})|(\d{10})|\d{9}(x))$/i);
    if (isbn_regex.test(keyword)) {
        return true;
    }
};

export function getFieldsToReturn() {
    return CSG_CONSTANTS.FIELDS_RETURN;
}


// WEBPACK FOOTER //
// ./src/_helpers/search/keywordSearch.js